/**
 * HTTP客户端
 * 封装axios，提供统一的请求和响应处理
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { API_CONFIG, HTTP_STATUS } from '../config/apiConfig';

// 响应数据类型定义
export interface ApiResponse<T = any> {
  code: number;
  message: string;
  data: T;
  timestamp: number;
}

// 分页响应数据类型定义
export interface PageResponse<T = any> {
  code: number;
  message: string;
  data: {
    records: T[];  // MyBatis-Plus 使用 records 字段
    total: number;
    current: number;
    size: number;
    pages: number;
  };
  timestamp: number;
}

// 请求配置类型
export interface RequestConfig extends AxiosRequestConfig {
  showLoading?: boolean;
  showError?: boolean;
  retry?: boolean;
}

class HttpClient {
  private instance: AxiosInstance;
  private requestQueue: Set<string> = new Set();
  private loadingCount = 0;

  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      headers: API_CONFIG.HEADERS,
      // 重要：允许跨域请求携带凭证（Cookie、Authorization header等）
      withCredentials: true,
      // 启用自动JSON转换（但跳过blob类型）
      transformResponse: [(data, headers) => {
        // 如果是blob类型，直接返回原始数据，不进行任何转换
        if (headers['content-type']?.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') ||
            headers['content-type']?.includes('application/octet-stream') ||
            headers['content-type']?.includes('application/vnd.ms-excel')) {
          return data;
        }
        
        if (typeof data === 'string') {
          try {
            return JSON.parse(data);
          } catch (e) {
            return data;
          }
        }
        return data;
      }],
    });

    this.setupInterceptors();
  }

  /**
   * 设置请求和响应拦截器
   */
  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        // 添加认证token
        const token = this.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // 添加请求ID用于去重
        const requestId = this.generateRequestId(config);
        (config as any).metadata = { requestId };

        // 显示加载状态
        if ((config as any).showLoading !== false) {
          this.showLoading();
        }

        return config;
      },
      (error) => {
        this.hideLoading();
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        this.hideLoading();
        return this.handleResponse(response) as any;
      },
      (error: AxiosError) => {
        this.hideLoading();
        return this.handleError(error);
      }
    );
  }

  /**
   * 处理响应
   */
  private handleResponse(response: AxiosResponse): ApiResponse {
    const { data } = response;
    
    // 如果是blob类型响应，直接返回原始数据
    if (response.config.responseType === 'blob') {
      return {
        code: HTTP_STATUS.OK,
        message: 'success',
        data: data,
        timestamp: Date.now(),
      };
    }
    
    // 如果响应数据没有code字段，说明是非标准响应格式（如健康检查）
    if (typeof data !== 'object' || data === null) {
      return {
        code: HTTP_STATUS.OK,
        message: 'success',
        data: data,
        timestamp: Date.now(),
      };
    }
    
    // 标准响应格式检查
    if (data.code !== undefined) {
      // 无论成功还是失败，都直接返回响应数据，让调用方处理
      return data;
    }
    
    // 没有code字段，说明是直接返回数据的接口
    return {
      code: HTTP_STATUS.OK,
      message: 'success',
      data: data,
      timestamp: Date.now(),
    };
  }

  /**
   * 处理错误
   */
  private handleError(error: AxiosError): Promise<never> {
    let message = '网络错误，请稍后重试';
    let shouldShowError = true;

    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case HTTP_STATUS.UNAUTHORIZED:
          // 从响应数据中获取错误消息，如果没有则使用默认消息
          message = (data as any)?.message || '用户已被禁用或登录已过期，请重新登录';
          
          // 获取请求的URL
          const requestUrl = error.config?.url || '';
          this.handleUnauthorized(requestUrl);
          
          // 检查是否是公开端点或网站端路径，如果是则不跳转，需要显示错误
          const publicEndpoints = [
            '/api/biz/news/list',
            '/api/biz/news/detail',
            '/api/biz/institution/list',
            '/api/biz/institution/detail',
            '/api/biz/showcase/list',
            '/api/biz/showcase/detail',
            '/api/biz/policy',
            '/api/biz/platform',
            '/api/biz/common-config',
            '/api/carousel',
            '/api/sys/dict',
          ];
          const isPublicEndpoint = requestUrl && publicEndpoints.some(endpoint => requestUrl.includes(endpoint));
          const isWebsitePath = window.location.pathname.startsWith('/show');
          
          // 如果是公开端点或网站端路径，不跳转，但也不显示错误（因为这是正常的）
          // 如果不是公开端点，不显示错误提示，因为已经会跳转到登录页
          shouldShowError = !(isPublicEndpoint || isWebsitePath);
          break;
        case HTTP_STATUS.FORBIDDEN:
          message = '权限不足，无法访问该资源';
          break;
        case HTTP_STATUS.NOT_FOUND:
          message = '请求的资源不存在';
          break;
        case HTTP_STATUS.BAD_REQUEST:
          message = (data as any)?.message || '请求参数错误';
          break;
        case HTTP_STATUS.INTERNAL_SERVER_ERROR:
          message = (data as any)?.message || '服务器内部错误';
          break;
        default:
          message = (data as any)?.message || `请求失败 (${status})`;
      }
    } else if (error.request) {
      // 请求已发送，但没有收到响应
      message = '网络连接失败，请检查网络设置或后端服务是否启动';
      console.error('No response received:', error.request);
    } else {
      // 请求配置出错
      message = error.message || '请求配置错误';
    }

    // 显示错误信息（可以通过config配置是否显示）
    if ((error.config as any)?.showError !== false && shouldShowError) {
      this.showError(message);
    }

    return Promise.reject(new Error(message));
  }

  /**
   * 处理未授权
   */
  private handleUnauthorized(url?: string) {
    // 检查是否是公开端点（网站端使用的API）
    // 如果是公开端点，不应该跳转到登录页
    const publicEndpoints = [
      '/api/biz/news/list',
      '/api/biz/news/detail',
      '/api/biz/institution/list',
      '/api/biz/institution/detail',
      '/api/biz/showcase/list',
      '/api/biz/showcase/detail',
      '/api/biz/policy',
      '/api/biz/platform',
      '/api/biz/common-config',
      '/api/carousel',
      '/api/sys/dict',
    ];
    
    // 检查当前URL是否是公开端点
    const isPublicEndpoint = url && publicEndpoints.some(endpoint => url.includes(endpoint));
    
    // 检查当前路径是否是网站端路径（/show）
    const isWebsitePath = window.location.pathname.startsWith('/show');
    
    // 如果是公开端点或网站端路径，不进行跳转
    if (isPublicEndpoint || isWebsitePath) {
      // 只是清除认证信息，不跳转
      localStorage.removeItem('admin_token');
      localStorage.removeItem('user_info');
      return;
    }
    
    // 清除本地存储的认证信息
    localStorage.removeItem('admin_token');
    localStorage.removeItem('user_info');
    
    // 触发认证状态变化事件（如果有的话）
    window.dispatchEvent(new CustomEvent('authStateChange', {
      detail: {
        isAuthenticated: false,
        user: null,
        token: null,
      }
    }));
    
    // 重定向到登录页面
    // 使用setTimeout确保在下一个事件循环中执行，避免与其他操作冲突
    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  }

  /**
   * 获取认证token
   */
  private getToken(): string | null {
    return localStorage.getItem('admin_token');
  }

  /**
   * 生成请求ID
   */
  private generateRequestId(config: AxiosRequestConfig): string {
    const { method, url, params, data } = config;
    return `${method}_${url}_${JSON.stringify(params)}_${JSON.stringify(data)}`;
  }

  /**
   * 显示加载状态
   */
  private showLoading() {
    this.loadingCount++;
    if (this.loadingCount === 1) {
      // 这里可以显示全局加载指示器
      document.body.classList.add('loading');
    }
  }

  /**
   * 隐藏加载状态
   */
  private hideLoading() {
    this.loadingCount = Math.max(0, this.loadingCount - 1);
    if (this.loadingCount === 0) {
      document.body.classList.remove('loading');
    }
  }

  /**
   * 显示错误信息
   */
  private showError(message: string) {
    // 控制台输出详细错误信息
    console.error('[HTTP Client Error]:', message);
    
    // 可以集成UI组件显示错误提示
    // 例如：toast.error(message) 或 message.error(message)
    // 这里暂时使用console，实际项目中应替换为UI提示组件
    if (typeof window !== 'undefined' && window.console) {
    }
  }

  /**
   * GET请求
   */
  async get<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.instance.get(url, config);
  }

  /**
   * POST请求
   */
  async post<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.instance.post(url, data, config);
  }

  /**
   * PUT请求
   */
  async put<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.instance.put(url, data, config);
  }

  /**
   * DELETE请求
   */
  async delete<T = any>(url: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.instance.delete(url, config);
  }

  /**
   * PATCH请求
   */
  async patch<T = any>(url: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    return this.instance.patch(url, data, config);
  }

  /**
   * 上传文件
   */
  async upload<T = any>(url: string, file: File, config?: RequestConfig): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    return this.instance.post(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    });
  }

  /**
   * 下载文件
   */
  async download(url: string, filename?: string, config?: RequestConfig): Promise<void> {
    const response = await this.instance.get(url, {
      ...config,
      responseType: 'blob',
    });

    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }

  /**
   * 取消请求
   */
  cancelRequest(requestId: string) {
    // 这里可以实现请求取消逻辑
    this.requestQueue.delete(requestId);
  }

  /**
   * 取消所有请求
   */
  cancelAllRequests() {
    this.requestQueue.clear();
  }
}

// 创建HTTP客户端实例
export const httpClient = new HttpClient();

// 类型定义已在types/index.ts中定义，这里不再重复导出
